import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CartItem, CartStoreState } from './cart.types'

export const useCartStore = create<CartStoreState>()(
  persist(
    (set, get) => ({
      cartId: null,
      items: [],
      apiCart: null,
      isSyncing: false,
      error: null,
      lastSynced: null,

      setCartId: (id) => set({ cartId: id }),
      setApiCart: (cart) => set({ apiCart: cart }),

      addItem: (product: Omit<CartItem, 'quantity'>, quantity = 1, max?: number) => {
        set((state) => {
          const existingItem = state.items.find((item) => item.id === product.id)
          const currentQty = existingItem ? existingItem.quantity : 0
          const requestedQty = currentQty + quantity

          // Enforce stock limit if max is provided
          const finalQty = max !== undefined ? Math.min(requestedQty, max) : requestedQty

          if (finalQty <= currentQty && existingItem) {
            return state // No change if already at max or invalid
          }

          let newItems: CartItem[]
          if (existingItem) {
            newItems = state.items.map((item) => (item.id === product.id ? { ...item, quantity: finalQty } : item))
          } else {
            newItems = [...state.items, { ...product, quantity: finalQty }]
          }

          return {
            items: newItems,
          }
        })
      },

      removeItem: (productId: string) => {
        set((state) => {
          const newItems = state.items.filter((item) => item.id !== productId)

          return {
            items: newItems,
          }
        })
      },

      updateQuantity: (productId: string, quantity: number, max?: number) => {
        const { removeItem } = get()

        if (quantity <= 0) {
          removeItem(productId)
          return
        }

        set((state) => {
          const finalQty = max !== undefined ? Math.min(quantity, max) : quantity
          const newItems = state.items.map((item) => (item.id === productId ? { ...item, quantity: finalQty } : item))

          return {
            items: newItems,
          }
        })
      },

      clearCart: () => {
        set({ items: [], apiCart: null })
      },
    }),
    {
      name: 'shopmuchmore-cart', // localStorage key
      partialize: (state) => ({ items: state.items, cartId: state.cartId }), // Persist items and cartId
    },
  ),
)

export const resetCartStore = () => {
  useCartStore.setState({ items: [], isSyncing: false, error: null, lastSynced: null, cartId: null, apiCart: null })
}

// --- Selectors ---

export const useCartItems = () => useCartStore((state) => state.items)
export const useCartCount = () => useCartStore((state) => state.items.reduce((acc, item) => acc + item.quantity, 0))
export const useCartSubtotal = () =>
  useCartStore((state) => state.items.reduce((acc, item) => acc + item.price * item.quantity, 0))

export const useItemQuantity = (productId: string) =>
  useCartStore((state) => state.items.find((item) => item.id === productId)?.quantity || 0)

export const useIsSyncing = () => useCartStore((state) => state.isSyncing)
